import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { syncManager } from '@/utils/syncManager';
import { offlineDB } from '@/utils/offlineDB';

export function OnlineStatusIndicator() {
    const isOnline = useOnlineStatus();
    const [pendingCount, setPendingCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        const updatePendingCount = async () => {
            try {
                const stats = await offlineDB.getStats();
                setPendingCount(stats.pendingOrders);
            } catch (error) {
                console.error('Error getting offline stats:', error);
            }
        };

        updatePendingCount();

        // Actualizar cada 10 segundos
        const interval = setInterval(updatePendingCount, 10000);

        // Escuchar eventos de sincronización
        const handleSyncStart = () => setIsSyncing(true);
        const handleSyncEnd = () => {
            setIsSyncing(false);
            updatePendingCount();
        };

        window.addEventListener('sync-start', handleSyncStart);
        window.addEventListener('sync-end', handleSyncEnd);
        window.addEventListener('connection-restored', updatePendingCount);

        return () => {
            clearInterval(interval);
            window.removeEventListener('sync-start', handleSyncStart);
            window.removeEventListener('sync-end', handleSyncEnd);
            window.removeEventListener('connection-restored', updatePendingCount);
        };
    }, []);

    const handleManualSync = async () => {
        setIsSyncing(true);
        await syncManager.syncAll();
        setIsSyncing(false);
    };

    if (isOnline && pendingCount === 0) {
        // Online y sin pendientes - indicador discreto
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Wifi className="w-4 h-4 text-green-400" />
                <span className="text-xs text-green-400 font-medium">Online</span>
            </div>
        );
    }

    if (isOnline && pendingCount > 0) {
        // Online con pendientes - mostrar botón sync
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <Wifi className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-yellow-400 font-medium">
                    {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''}
                </span>
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-yellow-400 hover:text-yellow-300"
                    onClick={handleManualSync}
                    disabled={isSyncing}
                >
                    <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
                </Button>
            </div>
        );
    }

    // Offline - banner prominente
    return (
        <div className="flex items-center gap-3 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg">
            <WifiOff className="w-5 h-5 text-red-400" />
            <div className="flex-1">
                <span className="text-sm text-red-400 font-bold block">Modo Offline</span>
                {pendingCount > 0 && (
                    <span className="text-xs text-red-300">
                        {pendingCount} pedido{pendingCount !== 1 ? 's' : ''} pendiente{pendingCount !== 1 ? 's' : ''} de sincronizar
                    </span>
                )}
            </div>
        </div>
    );
}
